// ABOUTME: Runs the realtime session for the signed-in identity and turns its events into cache
// ABOUTME: invalidations; publishes the poll interval its channel state implies (ADR-0001 §3.5).
import { useEffect, useReducer, useRef, useState, type FC, type PropsWithChildren } from "react"
import { useApolloClient } from "@apollo/client/react"
import config from "@/config"
import { SESSION_STORAGE_KEYS } from "@/model/auth/authSupport"
import {
  RealtimeLiveContext,
  RealtimePollIntervalContext,
  usePollIntervalForConnection,
  useUtcDayChange,
} from "@shared/hooks"
import { startRealtimeSession } from "@/model/realtime/session"
import { fetchRealtimeToken } from "@/model/realtime/token"
import { useMeContext } from "@/model/user/provider"
import { changesFromEvents, refreshFromEvent } from "@/model/calendar/cache/refreshCalendar"
import { handleDiscussionEvents, reloadDiscussions } from "@/model/discussion/discussionEvents"

/**
 * The session keys are shared between tabs, so a change from another tab means
 * this tab now speaks as somebody else and must not keep the old identity's
 * channel open. (The impersonation token lives in sessionStorage, per tab, and
 * changes only with a page reload.)
 */
const IDENTITY_STORAGE_KEYS = new Set<string>(SESSION_STORAGE_KEYS)

/** A login or a logout writes several of those keys in a row; one restart covers them. */
const IDENTITY_CHANGE_DEBOUNCE_MS = 500

/** Every open tab of every user crosses midnight UTC in the same second; this spreads their re-reads. */
const MIDNIGHT_JITTER_MS = 30_000

/**
 * One session per identity: keyed by `me.id` and by a generation bumped when
 * another tab changes the session tokens. Any change closes the old
 * connection explicitly before a new one opens; the session itself refuses a
 * token issued for anybody but `me.id`.
 */
export const RealtimeProvider: FC<PropsWithChildren> = ({ children }) => {
  const { me } = useMeContext()
  const client = useApolloClient()
  const [isConnected, setIsConnected] = useState(false)
  const [identityGeneration, bumpIdentityGeneration] = useReducer((generation: number) => generation + 1, 0)
  const sessionsStartedRef = useRef(0)
  const meId = me.id
  const pollInterval = usePollIntervalForConnection(isConnected)

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null
    const onStorage = (event: StorageEvent) => {
      // A null key is localStorage.clear() — a logout in another tab.
      if (event.key !== null && !IDENTITY_STORAGE_KEYS.has(event.key)) return
      if (timer !== null) clearTimeout(timer)
      timer = setTimeout(() => {
        timer = null
        bumpIdentityGeneration()
      }, IDENTITY_CHANGE_DEBOUNCE_MS)
    }
    window.addEventListener("storage", onStorage)
    return () => {
      if (timer !== null) clearTimeout(timer)
      window.removeEventListener("storage", onStorage)
    }
  }, [])

  useEffect(() => {
    const session = startRealtimeSession({
      endpoint: config.REALTIME_URL,
      userId: meId,
      getToken: () => fetchRealtimeToken(client),
      onEvents: (events) => {
        const changes = changesFromEvents(events)
        // A batch of the discussion's events alone changes no day of the calendar.
        if (changes.calendar.length > 0 || changes.health.length > 0) void refreshFromEvent(changes)
        handleDiscussionEvents(client, events)
      },
      onReload: () => {
        void refreshFromEvent(null)
        reloadDiscussions()
      },
      onConnectionChange: setIsConnected,
      // The page's first session follows the page's own load; a later one
      // has a gap behind it that nothing else covers.
      reloadOnFirstSubscribe: sessionsStartedRef.current > 0,
    })
    sessionsStartedRef.current += 1
    return () => {
      session.stop()
      setIsConnected(false)
    }
  }, [client, meId, identityGeneration])

  // A cache reset — a language switch drops the store (graphql/client/client.ts) — takes what no query on screen
  // reads back: an open feed's messages and the athletes' badges, which the feeds and the badge read write
  // themselves. Read again once the store is cleared, not waited on: the reset would hold back the refetch of
  // the queries on screen until they answer (ApolloClient.js:364-371).
  useEffect(
    () =>
      client.onResetStore(() => {
        reloadDiscussions()
        return Promise.resolve()
      }),
    [client],
  )

  // A workout hidden by the coach becomes visible at midnight UTC without any
  // write on the server, so no event announces it (ADR-0001 §3.3). Same refresh
  // as a channel that could not replay what was missed: what is on screen is
  // loaded again through the paths that loaded it in the first place.
  useUtcDayChange(() => {
    void refreshFromEvent(null)
  }, MIDNIGHT_JITTER_MS)

  return (
    <RealtimeLiveContext.Provider value={isConnected}>
      <RealtimePollIntervalContext.Provider value={pollInterval}>{children}</RealtimePollIntervalContext.Provider>
    </RealtimeLiveContext.Provider>
  )
}
